import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faStopwatch, faTimes, faLocationArrow, faMapMarkerAlt,
    faExclamationTriangle, faPlay, faPause, faStop, faSave
} from '@fortawesome/free-solid-svg-icons';

const TimesheetTimerPanel = ({
    showTimerPanel,
    setShowTimerPanel,
    timerRunning,
    timerElapsed,
    formatTime,
    locationStatus,
    userLocation,
    nearestSite,
    startTimer,
    pauseTimer,
    resumeTimer,
    stopTimer,
    timerChantier,
    setTimerChantier,
    chantiers,
    timerDescription,
    setTimerDescription,
    saveTimer
}) => {
    if (!showTimerPanel) return null;

    return (
        <div style={{
            position: 'fixed',
            bottom: '2rem',
            right: '2rem',
            background: 'var(--color-surface)',
            borderRadius: 'var(--border-radius-xl)',
            boxShadow: 'var(--shadow-xl)',
            border: `3px solid ${timerRunning ? '#ef4444' : '#10b981'}`,
            width: '350px',
            zIndex: 999,
            overflow: 'hidden'
        }}>
            {/* Header */}
            <div style={{
                background: timerRunning ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, #10b981, #059669)',
                color: 'white',
                padding: '1rem 1.5rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <FontAwesomeIcon icon={faStopwatch} />
                    Chronomètre
                </h4>
                <button
                    onClick={() => setShowTimerPanel(false)}
                    style={{
                        background: 'rgba(255, 255, 255, 0.2)',
                        border: 'none',
                        color: 'white',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>

            {/* Timer Display */}
            <div style={{
                padding: '2rem',
                textAlign: 'center',
                background: 'var(--color-background)'
            }}>
                <div style={{
                    fontSize: '3rem',
                    fontWeight: 'bold',
                    fontFamily: 'monospace',
                    color: timerRunning ? '#ef4444' : 'var(--color-text)',
                    marginBottom: '0.5rem',
                    letterSpacing: '2px'
                }}>
                    {formatTime(timerElapsed)}
                </div>
                <div style={{
                    fontSize: '0.9rem',
                    color: 'var(--color-text-light)',
                    marginBottom: '1rem'
                }}>
                    {timerElapsed > 0 ? `${Math.floor(timerElapsed / 3600)}h ${Math.floor((timerElapsed % 3600) / 60)}min` : 'Prêt à démarrer'}
                </div>

                {/* Geolocation Status UI */}
                <div style={{ marginBottom: '15px', padding: '10px', background: '#f8fafc', borderRadius: '8px', fontSize: '0.9rem' }}>
                    {locationStatus === 'locating' && (
                        <div style={{ color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={faLocationArrow} spin /> Géolocalisation en cours...
                        </div>
                    )}
                    {locationStatus === 'success' && userLocation && (
                        <div style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                            GPS: {userLocation.latitude.toFixed(4)}, {userLocation.longitude.toFixed(4)}
                            {nearestSite && <span style={{ marginLeft: '10px', fontWeight: 'bold' }}>📍 Proche: {nearestSite}</span>}
                        </div>
                    )}
                    {locationStatus === 'error' && (
                        <div style={{ color: '#ef4444', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={faExclamationTriangle} /> Erreur de localisation
                        </div>
                    )}
                </div>

                <div style={{ display: 'flex', gap: '15px', justifyContent: 'center' }}>
                    {!timerRunning && timerElapsed === 0 && (
                        <button
                            onClick={startTimer}
                            style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '1rem'
                            }}
                        >
                            <FontAwesomeIcon icon={faPlay} />
                            Démarrer
                        </button>
                    )}

                    {timerRunning && (
                        <button
                            onClick={pauseTimer}
                            style={{
                                background: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FontAwesomeIcon icon={faPause} />
                            Pause
                        </button>
                    )}

                    {!timerRunning && timerElapsed > 0 && (
                        <button
                            onClick={resumeTimer}
                            style={{
                                background: '#10b981',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FontAwesomeIcon icon={faPlay} />
                            Reprendre
                        </button>
                    )}

                    {timerElapsed > 0 && (
                        <button
                            onClick={stopTimer}
                            style={{
                                background: '#ef4444',
                                color: 'white',
                                border: 'none',
                                padding: '12px 24px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontWeight: '600',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <FontAwesomeIcon icon={faStop} />
                            Arrêter
                        </button>
                    )}
                </div>
            </div>

            {/* Form Section */}
            {timerElapsed > 0 && (
                <div style={{ padding: '1.5rem', borderTop: '1px solid var(--color-light-gray)' }}>
                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            color: 'var(--color-text)'
                        }}>
                            Chantier *
                        </label>
                        <select
                            value={timerChantier}
                            onChange={(e) => setTimerChantier(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid var(--color-light-gray)',
                                borderRadius: 'var(--border-radius-md)',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)',
                                fontSize: '0.95rem'
                            }}
                        >
                            <option value="">Sélectionner un chantier</option>
                            {chantiers.map(ch => (
                                <option key={ch} value={ch}>{ch}</option>
                            ))}
                        </select>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                        <label style={{
                            display: 'block',
                            marginBottom: '0.5rem',
                            fontWeight: '600',
                            fontSize: '0.9rem',
                            color: 'var(--color-text)'
                        }}>
                            Description
                        </label>
                        <textarea
                            value={timerDescription}
                            onChange={(e) => setTimerDescription(e.target.value)}
                            placeholder="Décrivez le travail effectué..."
                            rows="3"
                            style={{
                                width: '100%',
                                padding: '10px',
                                border: '1px solid var(--color-light-gray)',
                                borderRadius: 'var(--border-radius-md)',
                                backgroundColor: 'var(--color-surface)',
                                color: 'var(--color-text)',
                                fontSize: '0.95rem',
                                fontFamily: 'inherit',
                                resize: 'vertical'
                            }}
                        />
                    </div>

                    <button
                        onClick={saveTimer}
                        disabled={!timerChantier}
                        style={{
                            width: '100%',
                            background: timerChantier ? '#3b82f6' : '#9ca3af',
                            color: 'white',
                            border: 'none',
                            padding: '12px',
                            borderRadius: '8px',
                            cursor: timerChantier ? 'pointer' : 'not-allowed',
                            fontWeight: '600',
                            fontSize: '1rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                    >
                        <FontAwesomeIcon icon={faSave} />
                        Enregistrer ({parseFloat((timerElapsed / 3600).toFixed(2))}h)
                    </button>
                </div>
            )}
        </div>
    );
};

export default TimesheetTimerPanel;
